/**
 * Tour Management — grid column definitions and header button arrays.
 * Pure data, no React dependencies.
 */

import type { KstDataGridColumn, KstDataGridHeaderButton } from '@/components/KstDataGrid';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';
import {
  computeTourScheduleState,
  formatNextOpening,
  formatScheduleLine,
} from './tours/tourSchedule';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const durationFormatted = _wg('durationFormatted');

// ── Tour List ────────────────────────────────────────────────────────

export const tourListColumns: KstDataGridColumn[] = [
  {
    field: 'title', name: 'Name', size: 'col-xs-3',
    template: (e: any) => {
      const schedule = formatScheduleLine(e.availabilities || [], e.timeZoneId ?? null, new Date());
      return schedule ? `${e.title}<div class="kst-sched-line">${schedule}</div>` : `${e.title}`;
    },
  },
  { field: 'type', name: 'Type', size: 'col-xs-2' },
  { field: 'numberOfStops', name: 'Stops', size: 'col-xs-1' },
  {
    // Next Run: computed client-side from the tour's availability blocks, with
    // the same shared computation the editor preview and officer list use.
    field: 'nextRun', name: 'Next Run', size: 'col-xs-3',
    template: (e: any) => {
      if (!e.isEnabled) {
        return '<span class="kst-sched-pill kst-sched-pill--none">Disabled</span>';
      }
      // A tour with no attached stops never materializes a guard tour (server-side
      // creation gate), so an open availability window is not a run promise.
      if (e.numberOfStops === 0) {
        return '<span class="kst-sched-pill kst-sched-pill--none">No stops</span>' +
          '<div class="kst-sched-line">Will not run until a stop is added</div>';
      }
      const now = new Date();
      const state = computeTourScheduleState(e.availabilities || [], e.timeZoneId ?? null, now);
      if (state.status === 'in-progress') {
        return `<span class="kst-sched-pill kst-sched-pill--in">In progress</span>` +
          `<div class="kst-sched-line">Until ${formatNextOpening(state.end!, now)}</div>`;
      }
      if (state.status === 'upcoming') {
        return `<span class="kst-sched-pill kst-sched-pill--soon">Starts ${formatNextOpening(state.start!, now)}</span>`;
      }
      return '<span class="kst-sched-pill kst-sched-pill--none">No schedule</span>';
    },
  },
];

export const tourListHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

// ── Tour Stops for Tour ──────────────────────────────────────────────

export const toursTourStopsColumns: KstDataGridColumn[] = [
  { field: 'title', name: 'Name', size: 'col-xs-2' },
  { field: 'location', name: 'Location', size: 'col-xs-2' },
  {
    field: 'startDateTime', name: 'Active During', size: 'col-xs-2',
    template: (e: any) =>
      moment(e.startDateTime).format((window as any).DATE_FORMAT_DISPLAY_LOCAL_MOMENT).toString() + ' TO ' +
      moment(e.endDateTime).format((window as any).DATE_FORMAT_DISPLAY_LOCAL_MOMENT).toString(),
  },
  { field: 'numTours', name: '# Of Tours', size: 'col-xs-1' },
  { field: 'numTourStopTasks', name: 'Tasks', size: 'col-xs-1' },
  { field: 'numTourStopTasksTimeSensitive', name: 'Sensitive Tasks', size: 'col-xs-1' },
];

export const toursTourStopsHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add Global Tour Stop', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

// ── Global Tour Stops ────────────────────────────────────────────────

export const tourStopsColumns: KstDataGridColumn[] = [
  { field: 'title', name: 'Name', size: 'col-xs-2' },
  { field: 'location', name: 'Location', size: 'col-xs-2' },
  {
    field: 'startDateTime', name: 'Active During', size: 'col-xs-2',
    template: (e: any) =>
      moment(e.startDateTime).format((window as any).DATE_FORMAT_DISPLAY_LOCAL_MOMENT).toString() + ' TO ' +
      moment(e.endDateTime).format((window as any).DATE_FORMAT_DISPLAY_LOCAL_MOMENT).toString(),
  },
  { field: 'numTours', name: '# Of Tours', size: 'col-xs-1' },
  { field: 'numTourStopTasks', name: 'Tasks', size: 'col-xs-1' },
  { field: 'numTourStopTasksTimeSensitive', name: 'Sensitive Tasks', size: 'col-xs-2' },
];

export const tourStopHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

export const tourConnectedStopsHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add Selected Stops To Tour', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Cancel', function: 'back', src: '/Content/TourImages/skip-back.svg' },
];

// ── Tour Stop Tasks ──────────────────────────────────────────────────

export const tourStopTasksColumns: KstDataGridColumn[] = [
  { field: 'name', name: 'Name', size: 'col-xs-3' },
  { field: 'description', name: 'Description', size: 'col-xs-5' },
  {
    field: 'timeSensitiveList', name: 'Time Sensitive items', size: 'col-xs-3',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.timeSensitiveList)) {
        return e.timeSensitiveList.length;
      }
      return '';
    },
  },
];

export const tourStopTasksHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add Global Tasks', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

// ── Global Tasks ─────────────────────────────────────────────────────

export const taskColumns: KstDataGridColumn[] = [
  { field: 'name', name: 'Name', size: 'col-xs-3' },
  { field: 'defaultDescription', name: 'Description', size: 'col-xs-5' },
  {
    field: 'numAssociatedTourStops', name: 'Stops Associated', size: 'col-xs-3',
    template: (e: any) => `<a href="#" class="kst-link">${e.numAssociatedTourStops ?? 0}</a>`,
  },
];

export const tasksHeaderButton: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
  { name: 'Assign to Stops', function: 'assignToStops', src: '/Content/TourImages/add-r.svg' },
];

export const tasksGlobalHeaderButton: KstDataGridHeaderButton[] = [
  { name: 'Add Selected Tasks To Tour', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Cancel', function: 'back', src: '/Content/TourImages/skip-back.svg' },
];

// ── Task Tour Stops (Associated Stops Modal) ────────────────────────

export const taskTourStopsColumns: KstDataGridColumn[] = [
  { field: 'title', name: 'Name', size: 'col-xs-3' },
  { field: 'location', name: 'Location', size: 'col-xs-3' },
  { field: 'numTasks', name: 'Tasks', size: 'col-xs-2' },
  {
    field: 'tourNames', name: 'Tour(s)', size: 'col-xs-3',
    template: (e: any) => Array.isArray(e.tourNames) ? e.tourNames.join(', ') : '',
  },
];

// ── Assign to Stops Modal ───────────────────────────────────────────

export const assignToStopsColumns: KstDataGridColumn[] = [
  { field: 'title', name: 'Name', size: 'col-xs-3' },
  { field: 'location', name: 'Location', size: 'col-xs-3' },
  { field: 'numTourStopTasks', name: 'Tasks', size: 'col-xs-2' },
  { field: 'numTours', name: '# Of Tours', size: 'col-xs-2' },
];

// ── Tour Exemptions ──────────────────────────────────────────────────

export const tourExemptionColumns: KstDataGridColumn[] = [
  { field: 'revisionGroup', name: 'Id', size: 'hidden' },
  { field: 'name', name: 'Name', size: 'col-xs-4' },
  { field: 'description', name: 'Description', size: 'col-xs-5' },
];

export const tourExemptionHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Edit', function: 'edit', src: '/Content/TourImages/pen.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

// ── Tour Logs ────────────────────────────────────────────────────────

export const tourLogColumns: KstDataGridColumn[] = [
  {
    field: 'dateCreated', name: 'Date/Time', size: 'col-xs-3',
    template: (e: any) => e.dateCreated ? moment(e.dateCreated).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '',
  },
  { field: 'type', name: 'Type', size: 'col-xs-1' },
  { field: 'entry', name: 'Entry', size: 'col-xs-7' },
  { field: 'id', name: 'ID', size: 'col-xs-1' },
];

// ── Guard Tour History ───────────────────────────────────────────────

export function getGuardTourHistoryColumns(onShowStops: (id: number) => void): KstDataGridColumn[] {
  return [
    {
      field: 'title', name: 'Title', size: 'col-xs-2',
      template: (e: any) =>
        `<a onClick='window._tourMgmtShowGuardTourStops(${e.id}); return false;'>${e.title}</a>`,
    },
    { field: 'clientName', name: 'Client', size: 'col-xs-2' },
    {
      field: 'clientSiteNames', name: 'Sites', size: 'col-xs-3',
      template: (e: any) => {
        if (hasValue(e.clientSiteNames) && e.clientSiteNames.length > 0) {
          if (Array.isArray(e.clientSiteNames)) {
            return e.clientSiteNames.join(', ');
          }
          return e.clientSiteNames;
        }
        return '';
      },
    },
    {
      field: 'completedAt', name: 'Completed', size: 'col-xs-2',
      template: (e: any) => hasValue(e.completedAt) ? moment(e.completedAt).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '',
    },
    {
      field: 'duration', name: 'Duration', size: 'col-xs-1',
      template: (e: any) => durationFormatted(e.duration),
    },
    {
      field: 'issues', name: 'Issues', size: 'col-xs-2',
      template: (e: any) => hasValue(e.issues) ? `new: ${e.issues.new}, fixed: ${e.issues.fixed}, residual: ${e.issues.residual}` : '',
    },
  ];
}

// ── Guard Tour Stops for Guard Tour ──────────────────────────────────

export function getGuardTourStopsForGuardTourColumns(onShowStop: (id: number) => void): KstDataGridColumn[] {
  return [
    {
      field: 'title', name: 'Title', size: 'col-xs-2',
      template: (e: any) =>
        `<a onClick='window._tourMgmtShowGuardTourStop(${e.id}); return false;'>${e.title}</a>`,
    },
    { field: 'clientName', name: 'Client', size: 'col-xs-2' },
    { field: 'location', name: 'Location', size: 'col-xs-2' },
    {
      field: 'activeEndDateTime', name: 'End Date/Time', size: 'col-xs-2',
      template: (e: any) => hasValue(e.activeEndDateTime) ? moment(e.activeEndDateTime).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '',
    },
    {
      field: 'completedAt', name: 'Completed At', size: 'col-xs-2',
      template: (e: any) => hasValue(e.completedAt) ? moment(e.completedAt).format((window as any).DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT) : '',
    },
    {
      field: 'numTimesGuardTourStopCompleted', name: 'Times Completed', size: 'col-xs-2',
      template: (e: any) => `${e.numTimesGuardTourStopCompleted} / ${e.numTimesToCompleteGuardTourStop}`,
    },
  ];
}

// ── Mobile Tabs ──────────────────────────────────────────────────────

export const mobileTabs = [
  { id: 'showToursTab', label: 'Tours' },
  { id: 'showTourStopsTab', label: 'Stops' },
  { id: 'showTourStopTasksTab', label: 'Tasks' },
  { id: 'showExemptionsTab', label: 'Exceptions' },
  { id: 'showLogsTab', label: 'Logs' },
  { id: 'showGuardTourHistoryTab', label: 'History' },
  { id: 'showRemindersTab', label: 'Reminders' },
];
